import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Button,
  Collapse,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  FileDownload as DownloadIcon,
  PictureAsPdf as PdfIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  Science as ScienceIcon,
  AccessTime as TimeIcon,
  InsertDriveFile as FileIcon,
  TrackChanges as TargetIcon,
  AccountBalance as ObservatoryIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/apiService';

const StatCard = ({ title, value, subtitle, icon: Icon, color = 'primary' }) => (
  <Card>
    <CardContent>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography color="textSecondary" gutterBottom variant="body2">{title}</Typography>
          <Typography variant="h4" component="div" sx={{ my: 1 }}>{value}</Typography>
          {subtitle && <Typography variant="body2" color="textSecondary">{subtitle}</Typography>}
        </Box>
        <Box sx={{ bgcolor: `${color}.main`, color: 'white', borderRadius: 2, p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon />
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const TargetDetailRow = ({ targetName, scope, startDate, endDate, observatoryId }) => {
  const navigate = useNavigate();
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadDetails = async () => {
      try {
        setLoading(true);
        const params = { target_name: targetName, limit: 50 };
        if (scope) params.scope = scope;
        if (startDate) params.start_date = startDate.toISOString();
        if (endDate) params.end_date = endDate.toISOString();
        if (observatoryId) params.observatory_id = observatoryId;

        const response = await apiService.get('/v1/reporting/observations/by-target/detail', { params });
        if (response.data.success) {
          setDetails(response.data.data.observations);
        }
      } catch (err) {
        console.error('Failed to load target details:', err);
      } finally {
        setLoading(false);
      }
    };
    loadDetails();
  }, [targetName, scope, startDate, endDate, observatoryId]);

  const statusColors = {
    completed: 'success', failed: 'error', in_progress: 'warning',
    assigned: 'info', pending: 'default',
  };

  if (loading) return <TableRow><TableCell colSpan={7} align="center"><CircularProgress size={20} /></TableCell></TableRow>;
  if (!details) return null;

  return details.map((obs) => (
    <TableRow
      key={obs._id}
      hover
      sx={{ cursor: 'pointer', bgcolor: 'action.hover' }}
      onClick={() => navigate(`/observations/${obs._id}`)}
    >
      <TableCell sx={{ pl: 6 }}>{obs.name}</TableCell>
      <TableCell>
        <Chip label={obs.status} size="small" color={statusColors[obs.status] || 'default'} />
      </TableCell>
      <TableCell>{obs.observatory}</TableCell>
      <TableCell>{obs.created_at ? new Date(obs.created_at).toLocaleDateString() : ''}</TableCell>
      <TableCell>{obs.completed_at ? new Date(obs.completed_at).toLocaleDateString() : ''}</TableCell>
      <TableCell align="right">{obs.exposures_completed}</TableCell>
      <TableCell align="right">{obs.files}</TableCell>
    </TableRow>
  ));
};

const TargetObservationReport = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [results, setResults] = useState([]);
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 90);
    return d;
  });
  const [endDate, setEndDate] = useState(new Date());
  const [scope, setScope] = useState('user');
  const [groupByObservatory, setGroupByObservatory] = useState(false);
  const [expandedTargets, setExpandedTargets] = useState({});
  const [detailsLoaded, setDetailsLoaded] = useState({});

  const isAdmin = user?.role === 'server_admin' || user?.role === 'admin' || user?.role === 'instructor';

  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setExpandedTargets({});
      setDetailsLoaded({});
      const params = {};
      if (startDate) params.start_date = startDate.toISOString();
      if (endDate) params.end_date = endDate.toISOString();
      if (isAdmin) params.scope = scope;
      if (groupByObservatory) params.group_by_observatory = 'true';

      const response = await apiService.get('/v1/reporting/observations/by-target', { params });
      if (response.data.success) {
        setResults(response.data.data.results);
      }
    } catch (err) {
      const msg = typeof err.response?.data?.error === 'string'
        ? err.response.data.error
        : err.response?.data?.error?.message || err.message;
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, scope, isAdmin, groupByObservatory]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const getRowKey = (row) => {
    if (groupByObservatory && row.observatory_id) {
      return `${row.target_name}__${row.observatory_id}`;
    }
    return row.target_name;
  };

  const toggleTarget = (rowKey) => {
    setExpandedTargets(prev => ({ ...prev, [rowKey]: !prev[rowKey] }));
    if (!detailsLoaded[rowKey]) {
      setDetailsLoaded(prev => ({ ...prev, [rowKey]: true }));
    }
  };

  const exportCSV = async () => {
    try {
      const params = {};
      if (startDate) params.start_date = startDate.toISOString();
      if (endDate) params.end_date = endDate.toISOString();
      if (isAdmin) params.scope = scope;
      if (groupByObservatory) params.group_by_observatory = 'true';

      const response = await apiService.get('/v1/reporting/export/observations-by-target', {
        params,
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `target_observations_report.csv`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('CSV export failed:', err);
    }
  };

  const exportPDF = async () => {
    try {
      const params = {};
      if (startDate) params.start_date = startDate.toISOString();
      if (endDate) params.end_date = endDate.toISOString();
      if (isAdmin) params.scope = scope;
      if (groupByObservatory) params.group_by_observatory = 'true';

      const response = await apiService.get('/v1/reporting/export/observations-by-target-pdf', {
        params,
        responseType: 'blob'
      });

      // Open printable HTML report in new window
      const blob = new Blob([response.data], { type: 'text/html' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      console.error('PDF export failed:', err);
    }
  };

  // Summary stats
  const uniqueTargets = groupByObservatory
    ? new Set(results.map(r => r.target_name)).size
    : results.length;
  const uniqueObservatories = groupByObservatory
    ? new Set(results.map(r => r.observatory_id).filter(Boolean)).size
    : 0;
  const totalObs = results.reduce((s, r) => s + r.total_observations, 0);
  const totalHours = results.reduce((s, r) => s + (r.exposure_hours || 0), 0);
  const totalFiles = results.reduce((s, r) => s + (r.files || 0), 0);

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Target Observation Report</Typography>
        <Box display="flex" gap={1}>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={exportCSV}>CSV</Button>
          <Button variant="outlined" startIcon={<PdfIcon />} onClick={exportPDF}>PDF</Button>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchReport}>Refresh</Button>
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="From"
              value={startDate}
              onChange={setStartDate}
              slotProps={{ textField: { size: 'small', sx: { width: 160 } } }}
            />
            <DatePicker
              label="To"
              value={endDate}
              onChange={setEndDate}
              slotProps={{ textField: { size: 'small', sx: { width: 160 } } }}
            />
          </LocalizationProvider>

          {isAdmin && (
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Scope</InputLabel>
              <Select value={scope} onChange={(e) => setScope(e.target.value)} label="Scope">
                <MenuItem value="user">My Observations</MenuItem>
                <MenuItem value="observatory">My Observatories</MenuItem>
                <MenuItem value="all">All</MenuItem>
              </Select>
            </FormControl>
          )}

          <FormControlLabel
            control={
              <Switch
                checked={groupByObservatory}
                onChange={(e) => setGroupByObservatory(e.target.checked)}
                size="small"
              />
            }
            label="Group by Observatory"
          />
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Summary cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={groupByObservatory ? 2.4 : 3}>
              <StatCard title="Unique Targets" value={uniqueTargets} icon={TargetIcon} color="primary" />
            </Grid>
            {groupByObservatory && (
              <Grid item xs={12} sm={6} md={2.4}>
                <StatCard title="Observatories" value={uniqueObservatories} icon={ObservatoryIcon} color="warning" />
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={groupByObservatory ? 2.4 : 3}>
              <StatCard title="Total Observations" value={totalObs} icon={ScienceIcon} color="info" />
            </Grid>
            <Grid item xs={12} sm={6} md={groupByObservatory ? 2.4 : 3}>
              <StatCard title="Exposure Hours" value={totalHours.toFixed(1)} icon={TimeIcon} color="success" />
            </Grid>
            <Grid item xs={12} sm={6} md={groupByObservatory ? 2.4 : 3}>
              <StatCard title="Total Files" value={totalFiles} icon={FileIcon} color="secondary" />
            </Grid>
          </Grid>

          {/* Expandable target table */}
          <Paper>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Target Name</TableCell>
                    {groupByObservatory && <TableCell>Observatory</TableCell>}
                    <TableCell align="right">Observations</TableCell>
                    <TableCell align="right">Completed</TableCell>
                    <TableCell align="right">Failed</TableCell>
                    <TableCell align="right">Success %</TableCell>
                    <TableCell align="right">Exp Hours</TableCell>
                    <TableCell align="right">Files</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {results.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={groupByObservatory ? 8 : 7} align="center" sx={{ py: 4 }}>
                        <Typography color="textSecondary">No target data found for the selected period</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {results.map((row) => {
                    const rowKey = getRowKey(row);
                    return (
                    <React.Fragment key={rowKey}>
                      <TableRow
                        hover
                        sx={{ cursor: 'pointer', '& > *': { fontWeight: expandedTargets[rowKey] ? 'bold' : 'normal' } }}
                        onClick={() => toggleTarget(rowKey)}
                      >
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <IconButton size="small">
                              {expandedTargets[rowKey] ? <CollapseIcon /> : <ExpandIcon />}
                            </IconButton>
                            {row.target_name || 'Unknown'}
                          </Box>
                        </TableCell>
                        {groupByObservatory && <TableCell>{row.observatory_name || 'Unassigned'}</TableCell>}
                        <TableCell align="right">{row.total_observations}</TableCell>
                        <TableCell align="right">{row.completed}</TableCell>
                        <TableCell align="right">{row.failed}</TableCell>
                        <TableCell align="right">{(row.success_rate || 0).toFixed(1)}%</TableCell>
                        <TableCell align="right">{(row.exposure_hours || 0).toFixed(2)}</TableCell>
                        <TableCell align="right">{row.files || 0}</TableCell>
                      </TableRow>
                      {expandedTargets[rowKey] && (
                        <TableRow>
                          <TableCell colSpan={groupByObservatory ? 8 : 7} sx={{ p: 0 }}>
                            <Collapse in={expandedTargets[rowKey]} timeout="auto" unmountOnExit>
                              <Table size="small">
                                <TableHead>
                                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                                    <TableCell sx={{ pl: 6 }}>Observation</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Observatory</TableCell>
                                    <TableCell>Created</TableCell>
                                    <TableCell>Completed</TableCell>
                                    <TableCell align="right">Exposures</TableCell>
                                    <TableCell align="right">Files</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {detailsLoaded[rowKey] && (
                                    <TargetDetailRow
                                      targetName={row.target_name}
                                      observatoryId={groupByObservatory ? row.observatory_id : undefined}
                                      scope={isAdmin ? scope : undefined}
                                      startDate={startDate}
                                      endDate={endDate}
                                    />
                                  )}
                                </TableBody>
                              </Table>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default TargetObservationReport;
